import React, { Fragment, useEffect } from "react";
import logo from "./logo.png";
import defaultUser from "../assets/user.png";
import { Layout, Menu, Avatar, Typography, Badge, Tooltip } from "antd";
import {
  HomeOutlined,
  LogoutOutlined,
  CalendarOutlined,
  FileDoneOutlined,
  ContactsOutlined,
  GiftOutlined,
  MedicineBoxOutlined,
  SkinOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  UserOutlined,
  SendOutlined,
  NotificationOutlined,
  WalletOutlined,
  SwapOutlined,
  UsergroupAddOutlined,
  TeamOutlined,
  DashboardOutlined,
  ShopOutlined,
  ProfileOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { connect } from "react-redux";
import { ApolloResult, AppState, Clinic, User, actions } from "../store";
import paths from "../routes/paths";
import { useLazyQuery } from "@apollo/client";
import { getClinicVariables, get_clinics } from "../graphql/clinic";
import SubMenu from "antd/lib/menu/SubMenu";
import { appVersion } from "../constants";

const { Sider } = Layout;
const { Text } = Typography;

interface ModernSidebarProps {
  onMenuClick: (item: { key: string }) => void;
  isCollapsed: boolean;
  currentMenuItem?: string;
  currentClinic?: Clinic;
  clinicIds: string[];
  clinics: Clinic[];
  setClinics: (c: Clinic[]) => void;
  setSelectedClinic: (c: Clinic) => void;
  user?: User;
  logout: () => void;
}

interface MenuItem {
  key: string;
  label: string;
  icon: any;
  type: "item" | "submenu";
  badge?: string;
  children?: { key: string; label: string }[];
}

const menuItems: MenuItem[] = [
  {
    key: paths.home,
    label: "Home",
    icon: HomeOutlined,
    type: "item" as const,
  },
  {
    key: paths.appointments,
    label: "Appointments",
    icon: CalendarOutlined,
    type: "item" as const,
  },
  {
    key: paths.checkIns,
    label: "Check In/Out",
    icon: ClockCircleOutlined,
    type: "item" as const,
  },
  {
    key: "sales",
    label: "Sales Report",
    icon: FileDoneOutlined,
    type: "submenu" as const,
    children: [
      { key: paths.sales, label: "Sales" },
      { key: paths.paymentReport, label: "Payment Report" },
      { key: paths.salesBySalesPerson, label: "Sales by Sales Person" },
      { key: paths.customersBySalesPerson, label: "Customers by Sales Person" },
    ],
  },
  {
    key: paths.sale_channel,
    label: "Sale Channel",
    icon: ShopOutlined,
    type: "item" as const,
  },
  {
    key: "reports",
    label: "Reports",
    icon: FileTextOutlined,
    type: "submenu" as const,
    children: [
      { key: paths.daily_sales, label: "Daily Sale Report" },
      { key: paths.gtgram_sales, label: "GTGram Sales" },
      { key: paths.treatment_report, label: "Treatment Report" },
      { key: paths.top_treatment_report, label: "Top Treatment Report" },
      { key: paths.top_practitioner_report, label: "Top Practitioner Report" },
      { key: paths.seller_report, label: "Seller Report" },
      { key: paths.credit_received_report, label: "Credit Received Report" },
    ],
  },
  {
    key: "services",
    label: "Services",
    icon: MedicineBoxOutlined,
    type: "submenu" as const,
    children: [
      { key: paths.services, label: "Service List" },
      { key: paths.service_packages, label: "Service Packages" },
      { key: paths.service_categories, label: "Service Type Category" },
      { key: paths.serviceRooms, label: "Service Rooms" },
      { key: paths.service_record_froms, label: "Service Record Form" },
      { key: paths.service_consent_froms, label: "Service Consent Form" },
    ],
  },
  {
    key: "products",
    label: "Products",
    icon: SkinOutlined,
    type: "submenu" as const,
    children: [
      { key: paths.products, label: "Product List" },
      { key: paths.product_stock_items, label: "Product Stock Items" },
    ],
  },
  {
    key: "inventory",
    label: "Inventory",
    icon: SkinOutlined,
    type: "submenu" as const,
    badge: "New",
    children: [
      { key: "/inventory-history", label: "Inventory History" },
      { key: "/inventory-report", label: "Inventory Report" },
      { key: "/stock-summary", label: "Stock Summary" },
    ],
  },
  {
    key: paths.therapists,
    label: "Therapists",
    icon: ContactsOutlined,
    type: "item" as const,
  },
  {
    key: paths.helper,
    label: "Helper",
    icon: UsergroupAddOutlined,
    type: "item" as const,
  },
  {
    key: paths.members,
    label: "Members",
    icon: UserOutlined,
    type: "item" as const,
  },
  {
    key: paths.wallets,
    label: "Wallets",
    icon: WalletOutlined,
    type: "item" as const,
  },
  {
    key: paths.transactions,
    label: "Transactions",
    icon: SwapOutlined,
    type: "item" as const,
  },
  {
    key: "offers",
    label: "Offers",
    icon: SendOutlined,
    type: "submenu" as const,
    children: [
      { key: paths.offer_categories, label: "Offer Category" },
      { key: paths.offers, label: "Offer List" },
    ],
  },
  {
    key: paths.notifications,
    label: "Notifications",
    icon: NotificationOutlined,
    type: "item" as const,
  },
  {
    key: paths.leaves,
    label: "Leaves",
    icon: CalendarOutlined,
    type: "item" as const,
  },
  {
    key: paths.user,
    label: "Users",
    icon: TeamOutlined,
    type: "item" as const,
  },
  {
    key: paths.logs,
    label: "Logs",
    icon: ProfileOutlined,
    type: "item" as const,
  },
  {
    key: "settings",
    label: "Settings",
    icon: SettingOutlined,
    type: "submenu" as const,
    children: [
      { key: "general-settings", label: "General Settings" },
      { key: "security-settings", label: "Security Settings" },
      { key: "notification-settings", label: "Notification Settings" },
    ],
  },
  {
    key: "help",
    label: "Help & Support",
    icon: ProfileOutlined,
    type: "submenu" as const,
    children: [
      { key: "documentation", label: "Documentation" },
      { key: "faq", label: "FAQ" },
      { key: "contact-support", label: "Contact Support" },
    ],
  },
  {
    key: "analytics",
    label: "Analytics",
    icon: DashboardOutlined,
    type: "item" as const,
  },
  {
    key: "backup",
    label: "Backup & Restore",
    icon: FileDoneOutlined,
    type: "item" as const,
  },
  {
    key: "system",
    label: "System Info",
    icon: ProfileOutlined,
    type: "item" as const,
  },
];

function ModernSidebar(props: ModernSidebarProps) {
  const [getClinics, { data: cData, error: cError, loading: cLoading }] =
    useLazyQuery<ApolloResult<"clinics", Clinic[]>>(get_clinics, {
      variables: getClinicVariables(props.clinicIds),
    });


  useEffect(() => {
    if (cData?.clinics) {
      localStorage.setItem("clinics", JSON.stringify(cData.clinics));
      props.setClinics(cData.clinics);
    }
  }, [cData]);

  useEffect(() => {
    const localClinics = localStorage.getItem("clinics");
    if (localClinics != null) {
      const clinics = JSON.parse(localClinics);
      props.setClinics(clinics);
    } else {
      getClinics();
    }
  }, []);

  const renderMenuItems = () => {
    return menuItems.map((item) => {
      const IconComponent = item.icon;
      
      if (props.isCollapsed) {
        // Collapsed state - show only icons with tooltips
        return (
          <Tooltip
            key={item.key}
            title={item.label}
            placement="right"
            mouseEnterDelay={0.5}
          >
            <Menu.Item
              key={item.key}
              icon={<IconComponent />}
              className="modern-menu-item collapsed"
            >
              <span className="menu-label">{item.label}</span>
              {item.badge && (
                <Badge count={item.badge} size="small" className="menu-badge" />
              )}
            </Menu.Item>
          </Tooltip>
        );
      }

      if (item.type === "item") {
        // Regular menu item
        return (
          <Menu.Item
            key={item.key}
            icon={<IconComponent />}
            className="modern-menu-item"
          >
            <span className="menu-label">{item.label}</span>
            {item.badge && (
              <Badge count={item.badge} size="small" className="menu-badge" />
            )}
          </Menu.Item>
        );
      }

      if (item.type === "submenu" && item.children) {
        // Submenu with children
        return (
          <SubMenu
            key={item.key}
            icon={<IconComponent />}
            title={
              <span className="menu-label">
                {item.label}
                {item.badge && (
                  <Badge count={item.badge} size="small" className="menu-badge" />
                )}
              </span>
            }
            className="modern-submenu"
          >
            {item.children.map((child) => (
              <Menu.Item
                key={child.key}
                className="modern-submenu-item"
              >
                <span className="submenu-label">{child.label}</span>
              </Menu.Item>
            ))}
          </SubMenu>
        );
      }

      return null;
    });
  };
  return (
    <Sider
      className="modern-sidebar"
      collapsible
      collapsed={props.isCollapsed}
      trigger={null}
      width={280}
      collapsedWidth={80}
    >
      {/* Profile Section */}
      <div className="sidebar-profile">
        <div className="profile-content">
          <Avatar
            size={props.isCollapsed ? 40 : 50}
            src={props.user?.photo || defaultUser}
            className="profile-avatar"
            icon={<UserOutlined />}
            onError={(e: any) => {
              console.log("Avatar image failed to load:", e);
            }}

          />
          {!props.isCollapsed && (
            <div className="profile-info">
              <Text strong className="profile-name">
                {props.user?.name || "Admin"}
              </Text>
              <Text type="secondary" className="profile-role">
                Administrator
              </Text>
              <Text type="secondary" className="profile-version">
                {appVersion}
              </Text>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Menu */}
      <div className="sidebar-menu-container">
        <Menu
          theme="light"
          mode="inline"
          defaultSelectedKeys={
            props.currentMenuItem ? [props.currentMenuItem] : [paths.home]
          }
          onClick={props.onMenuClick}
          className="modern-menu"
        >
          {renderMenuItems()}
        </Menu>
      </div>

      {/* Footer Section */}
      <div className="sidebar-footer">
        {!props.isCollapsed && (
          <div className="footer-content">
            <Text type="secondary" className="footer-text">
              GT Report System
            </Text>
          </div>
        )}
        <div className="logout-container">
          <Tooltip
            title={props.isCollapsed ? "Logout" : ""}
            placement="right"
            mouseEnterDelay={0.5}
          >
            <Menu
              className="logout-menu"
              onClick={props.logout}
            >
              <Menu.Item
                icon={<LogoutOutlined />}
                className="logout-item"
              >
                {!props.isCollapsed && <span>Logout</span>}
              </Menu.Item>
            </Menu>
          </Tooltip>
        </div>
      </div>
    </Sider>
  );
}

const mapStateToDispatch = (dispatch: any) => {
  return {
    setClinics: (d: Clinic[]) => dispatch(actions.clinicAction.setClinics(d)),
    setSelectedClinic: (d: Clinic) =>
      dispatch(actions.clinicAction.setSelected(d)),
    logout: () => dispatch(actions.authAction.loggedOut()),
  };
};

const mapStateToProps = (state: AppState) => {
  return {
    isCollapsed: state.antdState.openSideToogle,
    currentClinic: state.clinicState.selected,
    clinicIds:
      state.authState.status === "loggedIn"
        ? state.authState.userInfo?.clinics || []
        : [],
    clinics: state.clinicState.clinics,
    user:
      state.authState.status === "loggedIn"
        ? state.authState.userInfo!
        : undefined,
  };
};

export default connect(mapStateToProps, mapStateToDispatch)(ModernSidebar) as any;
